import { TranslateQuery } from "@omni/types/api/reviews/bff/request/translate.query";
import { type Review as TReview } from "@omni/types/api/reviews/bff/response/review.model";
import { TranslateReview } from "@omni/types/api/reviews/bff/response/translateReview.model";
import { Button } from "@omni/ui/components/Button/button.component";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import { useState } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { Modal } from "@/components/General/Modal/modal";
import { PopOver } from "@/components/General/PopOver/popOver.component";
import { ImageGallery } from "@/components/ImageGallery/imageGallery";
import { Rating } from "@/components/Product/Rating/rating.component";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { Config } from "@/utils/config.utils";
import { safeParseISO } from "@/utils/date";
import { formatRelativeDate } from "@/utils/format";

import { Feedback } from "./components/feedback/feedback.component";
import { SyndicationSource } from "./components/syndicationSource/syndicationSource.component";
import { WouldRecommend } from "./components/wouldRecommend/wouldRecommend.component";

type ReviewProps = {
    range: number;
    review: TReview;
    showLink: boolean;
    showProductImage: boolean;
};

export const Review = ({
    range,
    review,
    showLink,
    showProductImage
}: ReviewProps) => {
    const { t } = useGranite();

    const [, getTranslations] = useBFFProductServiceApi<
        undefined,
        undefined,
        TranslateReview
    >(
        {
            url: `/reviews/translations`,
            method: "GET"
        },
        {
            manual: true,
            useCache: false
        }
    );

    const [translated, setTranslated] = useState<boolean>(false);

    const [displayTitle, setDisplayTitle] = useState(review.title);
    const [displayText, setDisplayText] = useState(review.text);

    const activeIndexState = useState<number>(0);
    const [, setActiveImageIndex] = activeIndexState;
    const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);

    const translatable = review.locale !== Config.getLocale();

    const handleOnTranslateClick = async () => {
        if (translated) {
            setDisplayTitle(review.title);
            setDisplayText(review.text);
            setTranslated(false);
        } else {
            const request: TranslateQuery = {
                reviewId: review.id,
                sku: review.productSku
            };

            const { data } = await getTranslations({
                params: request
            });

            if (isDefined(data)) {
                setDisplayTitle(data.title);
                setDisplayText(data.text);
                setTranslated(true);
            }
        }
    };

    const submissionTime = safeParseISO(review.submissionTime);
    const displaySubmissionTime = submissionTime
        ? formatRelativeDate(submissionTime, Config.getLocale())
        : null;

    return (
        <Row>
            {showProductImage && (
                <Col
                    xs={12}
                    md={3}
                >
                    <img
                        className={cn("tw:mb-4 tw:max-w-50 tw:w-full")}
                        src={review.productImage}
                        alt={review.productName}
                    />
                </Col>
            )}
            <Col
                xs={12}
                md={showProductImage ? 9 : 12}
                className={cn("tw:gap-4 tw:flex tw:flex-col")}
            >
                <div className={cn("tw:flex tw:gap-2")}>
                    <Rating
                        value={review.rating}
                        max={range}
                        size={16}
                    />
                    {displaySubmissionTime && (
                        <div className={cn("tw:italic")}>
                            {displaySubmissionTime}
                        </div>
                    )}
                </div>
                {review.isVerifiedPurchase && (
                    <PopOver
                        dismissable={false}
                        onHover={true}
                        onClick={false}
                        anchorComponent={(
                            setReference,
                            getReferenceProps,
                            popOverComponent
                        ) => (
                            <>
                                <div
                                    ref={setReference}
                                    className={cn(
                                        "tw:px-1 tw:bg-primary tw:text-white tw:rounded-sm tw:w-fit tw:font-bold"
                                    )}
                                    {...getReferenceProps()}
                                >
                                    {t(
                                        "product.reviews.review.verifiedPurchase.label"
                                    )}
                                </div>
                                {popOverComponent()}
                            </>
                        )}
                    >
                        <p>
                            {t(
                                "product.reviews.review.verifiedPurchase.tooltip"
                            )}
                        </p>
                    </PopOver>
                )}
                <p className={cn("tw:font-bold")}>{displayTitle}</p>
                {showLink && (
                    <p className={cn("tw:m-0")}>
                        <a href={review.productPageUrl}>{review.productName}</a>
                    </p>
                )}
                <p className={cn("tw:mb-0 tw:wrap-break-word")}>
                    {displayText}
                    {translatable && (
                        <Button
                            className={cn("tw:p-0 tw:w-fit tw:block")}
                            color={"link"}
                            onClick={handleOnTranslateClick}
                        >
                            {!translated &&
                                t("product.reviews.review.translate")}
                            {translated &&
                                t("product.reviews.review.showOriginal")}
                        </Button>
                    )}
                </p>
                {!!review.images.length && (
                    <div
                        className={cn("tw:flex tw:flex-wrap tw:gap-4 tw:my-4")}
                    >
                        {review.images.map((image, index) => (
                            <img
                                className={cn("tw:object-cover")}
                                onClick={() => {
                                    setImageModalOpen(true);
                                    setActiveImageIndex(index);
                                }}
                                key={index}
                                width={"150"}
                                height={"150"}
                                src={image.url}
                                alt={image.altText ?? undefined}
                            />
                        ))}
                        <Modal
                            title={displayTitle}
                            open={imageModalOpen}
                            setOpen={setImageModalOpen}
                            className={cn("tw:m-auto")}
                        >
                            <ImageGallery
                                className={cn(
                                    "tw:my-4 tw:flex tw:flex-col tw:relative"
                                )}
                                images={review.images}
                                activeIndexState={activeIndexState}
                            >
                                <ImageGallery.MainSwiper
                                    className={cn("tw:w-full tw:max-h-[50dvh]")}
                                />
                                <div className={cn("tw:flex tw:mt-4")}>
                                    <ImageGallery.PreviousButton />
                                    <ImageGallery.ThumbSwiper
                                        className={cn("tw:h-20")}
                                    />
                                    <ImageGallery.NextButton />
                                </div>
                            </ImageGallery>
                        </Modal>
                    </div>
                )}
                {isDefined(review.wouldRecommend) && (
                    <WouldRecommend wouldRecommend={review.wouldRecommend} />
                )}
                <Feedback
                    reviewId={review.id}
                    feedback={review.feedback}
                />
                <SyndicationSource
                    syndicationSource={review.syndicationSource}
                />
            </Col>
        </Row>
    );
};
